// Payments/PaymentLinksTable.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, CircularProgress, Alert, Box, Chip, IconButton, Tooltip,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Switch, FormControlLabel, Typography, Card, CardContent, List,
  ListItem, ListItemText, Stack, alpha, useTheme, Grid
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BarChartIcon from '@mui/icons-material/BarChart';
import QrCodeIcon from '@mui/icons-material/QrCode';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const PaymentLinksTable = ({ refreshKey, onRefresh, permission }) => {
  const theme = useTheme();
  const [links, setLinks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [statsDialog, setStatsDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, [page, rowsPerPage, refreshKey]);

  const fetchLinks = async () => {
    setIsLoading(true);
    try {
      const params = { page: page + 1, per_page: rowsPerPage };
      const response = await axiosInstance.get(`/payments/payment-links`, { params });
      setLinks(response.data.data);
      setTotalRows(response.data.total);
      setError('');
    } catch (err) {
      setError('Failed to fetch payment links.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (event, link) => {
    setAnchorEl(event.currentTarget);
    setSelectedLink(link);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLink(null);
  };

  const handleCopyLink = () => {
    if (selectedLink) {
      const link = `${window.location.origin}/pay/${selectedLink.token}`;
      navigator.clipboard.writeText(link).then(() => {
        alert('Payment link copied to clipboard!');
      });
    }
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    try {
      await axiosInstance.patch(
        `/payments/payment-links/${selectedLink.id}/toggle-status`,
        {}
      );
      fetchLinks();
      if (onRefresh) onRefresh();
    } catch (err) {
      setError('Failed to update link status.');
    }
    handleMenuClose();
  };

  const handleDeleteLink = async () => {
    try {
      await axiosInstance.delete(`/payments/payment-links/${selectedLink.id}`);
      fetchLinks();
      if (onRefresh) onRefresh();
      setDeleteDialog(false);
    } catch (err) {
      setError('Failed to delete payment link.');
    }
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { color: 'success', label: 'Active' },
      inactive: { color: 'default', label: 'Inactive' },
      expired: { color: 'error', label: 'Expired' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const getPaymentTypeChip = (type) => {
    const typeConfig = {
      donation: { color: 'primary', label: 'Donation' },
      signup: { color: 'secondary', label: 'Signup' },
      subscription: { color: 'info', label: 'Subscription' },
      product: { color: 'warning', label: 'Product' },
      others: { color: 'default', label: 'Others' }
    };
    const config = typeConfig[type] || typeConfig.others;
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress size={30} />
        <Typography mt={2}>Loading payment links...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <i className="fa fa-exclamation-triangle" /> {error}
      </Alert>
    );
  }

  if (links.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <i className="fa fa-link fa-3x text-muted" />
        <Typography mt={2} color="text.primary">
          No payment links available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first payment link to get started
        </Typography>
      </Box>
    );
  }

  return (
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          maxWidth: '100%',
          mx: 'auto', // centers inside page
        }}
      >
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            borderRadius: 2,
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 600,
              overflowX: 'auto',
              width: '100%',
              // FIX: Added custom scrollbar styling that adapts to the theme
              scrollbarWidth: 'thin',
              scrollbarColor: `${theme.palette.divider} ${theme.palette.background.paper}`,
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.background.paper,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.divider,
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              },
            }}
          >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 200 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 120 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 150 }}>Recurring</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 150 }}>Amounts</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 120 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 120 }}>Stats</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.05), minWidth: 80 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link) => (
                <TableRow 
                  hover 
                  key={link.id}
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                >
                  <TableCell>
                    <Box sx={{ maxWidth: 250 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {link.title}
                      </Typography>
                      {link.description && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {link.description.length > 50 
                            ? `${link.description.substring(0, 50)}...` 
                            : link.description
                          }
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{getPaymentTypeChip(link.payment_type)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {link.payment_frequencies.onetime && (
                        <Chip size="small" variant="outlined" label="Onetime" />
                      )}
                      {link.payment_frequencies.recurring && (
                        <Chip size="small" variant="outlined" label="Recurring" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {link.suggested_amounts.slice(0, 2).map((amount, index) => (
                        <Chip key={index} size="small" label={`$${amount}`} variant="outlined" />
                      ))}
                      {link.suggested_amounts.length > 2 && (
                        <Chip size="small" label={`+${link.suggested_amounts.length - 2}`} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{getStatusChip(link.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {new Date(link.created_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="caption" display="block" noWrap>
                        Views: <strong>{link.view_count || 0}</strong>
                      </Typography>
                      <Typography variant="caption" display="block" noWrap>
                        Payments: <strong>{link.payment_count || 0}</strong>
                      </Typography>
                      <Typography variant="caption" display="block" color="primary" noWrap>
                        Revenue: <strong>${link.total_revenue || '0.00'}</strong>
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={(event) => handleMenuClick(event, link)}
                      size="small"
                      color="primary"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCopyLink}>
          <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
          Copy Link
        </MenuItem>
        <MenuItem onClick={() => { setStatsDialog(true); handleMenuClose(); }}>
          <BarChartIcon sx={{ mr: 1 }} fontSize="small" />
          View Stats
        </MenuItem>

        {permission.canEdit && (
          <>
            <MenuItem onClick={() => { setEditDialog(true); handleMenuClose(); }}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Edit
            </MenuItem>
            <MenuItem onClick={handleToggleStatus}>
              {selectedLink?.status === 'active' ? (
                <>
                  <VisibilityOffIcon sx={{ mr: 1 }} fontSize="small" />
                  Deactivate
                </>
              ) : (
                <>
                  <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
                  Activate
                </>
              )}
            </MenuItem>
          </>
        )}
        {permission.canDelete && (
          <MenuItem onClick={() => { setDeleteDialog(true); handleMenuClose(); }} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Stats Dialog */}
      <Dialog open={statsDialog} onClose={() => setStatsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Payment Link Statistics</DialogTitle>
        <DialogContent>
          {selectedLink && <PaymentLinkStats link={selectedLink} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {permission.canEdit && (
        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>Edit Payment Link</DialogTitle>
          <DialogContent>
            {selectedLink && (
              <EditPaymentLinkForm 
                link={selectedLink} 
                onSave={() => {
                  setEditDialog(false);
                  fetchLinks();
                  if (onRefresh) onRefresh();
                }} 
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Payment Link</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedLink?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteLink} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Stats Component
const PaymentLinkStats = ({ link }) => {
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get(
          `/payments/payment-links/${link.id}/stats`
        );
        setStats(response.data.stats);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [link.id]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom fontWeight={600}>{link.title}</Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight={700}>
                {stats?.total_views || 0}
              </Typography>
              <Typography variant="body2" color="textPrimary">Total Views</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight={700}>
                {stats?.total_payments || 0}
              </Typography>
              <Typography variant="body2" color="textPrimary">Total Payments</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="secondary.main" fontWeight={700}>
                ${stats?.total_revenue || '0.00'}
              </Typography>
              <Typography variant="body2" color="textPrimary">Total Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" fontWeight={700}>
                {stats?.conversion_rate || '0'}%
              </Typography>
              <Typography variant="body2" color="textPrimary">Conversion Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {stats?.recent_payments && stats.recent_payments.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom fontWeight={600}>Recent Payments</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.recent_payments.map((payment, index) => (
                  <TableRow key={index}>
                    <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                    <TableCell>{payment.customer_name || payment.customer_email}</TableCell>
                    <TableCell fontWeight={600}>${payment.amount}</TableCell>
                    <TableCell>
                      <Chip size="small" color="success" label={payment.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

// Edit Form Component
const EditPaymentLinkForm = ({ link, onSave }) => {
  const [formData, setFormData] = useState({
    title: link.title,
    description: link.description,
    is_active: link.status === 'active',
    expires_at: link.expires_at || ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axiosInstance.patch(
        `/payments/payment-links/${link.id}`,
        formData
      );
      onSave();
    } catch (err) {
      console.error('Failed to update payment link:', err);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <TextField
        fullWidth
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        sx={{ mb: 2 }}
        required
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        type="datetime-local"
        label="Expires At"
        name="expires_at"
        value={formData.expires_at}
        onChange={handleInputChange}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={formData.is_active}
            onChange={handleInputChange}
            name="is_active"
          />
        }
        label="Active"
        sx={{ mb: 2 }}
      />
      <Button type="submit" variant="contained" fullWidth size="large">
        Save Changes
      </Button>
    </Box>
  );
};

export default PaymentLinksTable;